<template>
  <v-card class="bm-card" v-bind="$attrs" :width="width" :height="height">
    <v-card-title
      v-if="label"
      class="bm-card-title bm-heading3"
      :class="{ 'smaller-font': smallerTitleFont }"
      :style="{
        'background-color': titleBackgroundColour,
      }"
      @click="emit('header-click')"
    >
      <v-row
        no-gutters
        justify="center"
        align="center"
        :class="{ 'row-no-overflow': truncateTitle }"
      >
        <v-col cols="auto" class="text-left fit-content">
          <slot name="prepend" />
        </v-col>

        <v-tooltip v-if="truncateTitle" top>
          <!-- Truncate title and show tooltip on hover -->
          <template #activator="{ on, attrs }">
            <v-col
              class="mr-2 overflow-hidden d-flex align-center"
              v-bind="attrs"
              v-on="on"
            >
              <div v-if="$slots['prepend-label']" class="mr-1">
                <slot name="prepend-label" />
              </div>
              <p class="text-truncate my-auto ml-1">
                {{ label }}
              </p>
            </v-col>
          </template>
          <span class="d-flex align-center">
            <div v-if="$slots['prepend-label']" class="mr-4">
              <slot name="prepend-label" />
            </div>
            {{ label }}
          </span>
        </v-tooltip>

        <v-col
          v-else
          class="d-flex align-center"
          :class="{ 'mr-2': !smallerTitleFont }"
        >
          <div v-if="$slots['prepend-label']" class="mr-4">
            <slot name="prepend-label" />
          </div>
          {{ label }}
        </v-col>

        <v-col cols="1" class="text-right fit-content">
          <slot name="menu">
            <a
              v-if="
                visibleMenuItems.length === 1 &&
                !menu.showPopupMenuForSingleItem
              "
              v-show="
                !visibleMenuItems[0].isVisible ||
                visibleMenuItems[0].isVisible()
              "
              role="button"
              tabindex="0"
              class="bm-link"
              data-qa="card-menu-button"
              @click.stop="visibleMenuItems[0].onClick"
            >
              {{ visibleMenuItems[0].displayText }}</a
            >

            <v-menu v-else-if="visibleMenuItems.length" offset-y>
              <template #activator="{ on }">
                <v-btn
                  data-qa="card-menu-button"
                  small
                  icon
                  style="margin-top: -3px"
                  v-on="on"
                >
                  <bm-icon icon="MoreVert" />
                </v-btn>
              </template>
              <v-list data-qa="card-menu">
                <v-list-item
                  v-for="(menuItem, index) of visibleMenuItems"
                  v-show="!menuItem.isVisible || menuItem.isVisible()"
                  :key="index"
                  @click="menuItem.onClick && menuItem.onClick()"
                >
                  <v-list-item-title>{{
                    menuItem.displayText
                  }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </slot>
        </v-col>
      </v-row>
    </v-card-title>

    <div
      v-if="showTopControls"
      v-show="!hideControls && !!$slots['controls']"
      class="no-print"
    >
      <v-card-actions>
        <slot name="controls" />
      </v-card-actions>
      <v-divider />
    </div>

    <v-card-text :class="{ 'py-0': dense }">
      <slot />
    </v-card-text>

    <!-- slot data is not reactive https://github.com/vuejs/vue/issues/3517, so won't work off of a computed property -->
    <div v-show="!hideControls && !!$slots['controls']" class="no-print">
      <v-divider />
      <v-card-actions>
        <slot name="controls" />
      </v-card-actions>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type CardMenuDefinition from "@/components/controls/card/CardMenuDefinition";

/**
 * General purpose card that is really just for styling.
 */
interface BmCardProps {
  label?: string;
  hideControls?: boolean;
  titleBackgroundColour?: string;
  menu?: CardMenuDefinition;
  truncateTitle?: boolean;
  smallerTitleFont?: boolean;
  dense?: boolean;
  showTopControls?: boolean;
  width?: number;
  height?: number;
}

const props = withDefaults(defineProps<BmCardProps>(), {
  hideControls: false,
  menu: () => ({ menuItems: [] }) as CardMenuDefinition,
  truncateTitle: false,
  smallerTitleFont: false,
  dense: false,
  showTopControls: false,
});

interface BmCardEvents {
  (eventName: "header-click"): void;
}
const emit = defineEmits<BmCardEvents>();

const visibleMenuItems = computed(() =>
  props.menu.menuItems.filter((mi) => !mi.isVisible || mi.isVisible())
);
</script>

<style scoped lang="sass">
@import "src/sass/colours"

.bm-card
  justify-content: space-between

.bm-card .bm-card-title
  background-color: $bm-color-background04
  word-break: break-word

.bm-card-title
  padding-top: 12px
  padding-bottom: 12px
  margin-bottom: 10px
  color: $bm-color-brand01

.row-no-overflow
  overflow: hidden
  white-space: nowrap

.fit-content
  min-width: fit-content

a
  text-decoration: underline
  white-space: nowrap
  padding-left: 5px

.smaller-font
  font-size: 16px !important

@media print
// Prevent cards from splitting across pages
  .bm-card,
  :deep(.v-card)
    page-break-inside: avoid !important
    break-inside: avoid !important
</style>
